/**
 * Canonical Deal UI tokens.
 *
 * Feature screens must consume these tokens or the System* components instead
 * of introducing local colours, radii, control heights or shadows. Keeping the
 * object dependency-free also makes it usable from styled-components, inline
 * styles and the remaining legacy MUI theme adapters during migration.
 */
export const dealTokens = Object.freeze({
  color: Object.freeze({
    primary: '#E6616A',
    primaryHover: '#D94D61',
    primarySoft: '#FDECEE',
    text: '#252525',
    textMuted: '#73747A',
    textSubtle: '#898B8D',
    surface: '#FFFFFF',
    canvas: '#F7F7F8',
    border: '#E8E8E8',
    borderStrong: '#DFE2EA',
    danger: '#DC2626',
    dangerSoft: '#FEF2F2',
    success: '#469B56',
    successSoft: '#EEF7F0',
    warning: '#D97706',
    warningSoft: '#FFF7E8',
    info: '#5567E6',
    infoSoft: '#EEF0FF',
    // Standing explanatory notes (PageIntro). A sky blue kept apart from `info`, which is
    // the Edit action colour, so a note never reads as related to a button. `note` is
    // ~3.6:1 on `noteSoft` and ~3.8:1 on `canvas` (non-text 3:1 minimum).
    note: '#0284C7',
    noteSoft: '#E0F2FE',
    disabled: '#E8E8E8',
    // Preparation buffer band at the bottom of week/day calendar events (the part of a service
    // or session that is not the customer's time). Same grey the Prod1 calendar used.
    preparation: '#D3D3D3',
  }),
  font: Object.freeze({
    family: 'Metropolis, sans-serif',
    weightRegular: 400,
    weightMedium: 500,
    weightSemibold: 600,
    weightBold: 700,
  }),
  space: Object.freeze({
    1: 4,
    2: 8,
    3: 12,
    4: 16,
    5: 20,
    6: 24,
    8: 32,
  }),
  radius: Object.freeze({
    sm: 6,
    md: 8,
    lg: 12,
    pill: 999,
  }),
  control: Object.freeze({
    sm: 28,
    md: 36,
    lg: 44,
  }),
  shadow: Object.freeze({
    none: 'none',
    subtle: '0 1px 3px rgba(37, 37, 37, 0.08)',
    floating: '0 8px 24px rgba(37, 37, 37, 0.12)',
  }),
  motion: Object.freeze({
    fast: '120ms ease',
    normal: '150ms ease',
  }),
  layout: Object.freeze({
    pageGap: 24,
    sectionGap: 16,
    cardPadding: 16,
    modalHeaderPadding: '18px 22px 14px',
    modalBodyPadding: '18px 22px',
    modalFooterPadding: '14px 22px 18px',
  }),
});

export const semanticActionColors = Object.freeze({
  create: dealTokens.color.primary,
  edit: dealTokens.color.info,
  delete: dealTokens.color.danger,
  archive: dealTokens.color.warning,
  restore: dealTokens.color.success,
  view: dealTokens.color.textMuted,
});

export default dealTokens;
